/**
 * @file 加入知识库弹窗
 * 空间/团队/KB 数据源：useKnowledgeBaseAddModalSpace
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Modal } from 'tea-component';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { downDataTip } from '@/pages/aigc/components/DownloadTips';
import {
  useCreateKnowledgeBase,
  useCreateKnowledgeDocument,
  useKbDefaultModels,
  type IKnowledgeBaseAddFormValues,
} from '@/pages/aigc/pages/Agent/components/QAInput/KnowledgeBaseMention/hooks/useKnowledgeBaseApi';
import { type ISearchRef } from '@gp/pages/Pipeline/ProjectDetails/components/GameOverviewEdit/Components/UseFormSearch';
import {
  useKnowledgeBaseAddFormConfig,
  type TKnowledgeAddTab,
} from './hooks/useKnowledgeBaseAddFormConfig';
import type { TKnowledgeSpace } from './components/KbAddFormControls';
import { useKnowledgeBaseAddModalSpace } from './hooks/useKnowledgeBaseAddModalSpace';
import KbAddFormSearch from './components/KbAddFormSearch';
import { handleOPenUrl } from '../SubComp';
import {
  buildDefaultCreateKbPayload,
  buildGameWikiKbDetailUrl,
  resolveKbDisplayName,
  isEditableHtmlEmpty,
  convertHtmlToMarkdown,
  stripAnswerProtocolTags,
} from './utils';
import './index.scss';

export type { IKnowledgeBaseAddFormValues } from '@/pages/aigc/pages/Agent/components/QAInput/KnowledgeBaseMention/hooks/useKnowledgeBaseApi';

export interface IKnowledgeBaseAddModalProps {
  visible: boolean;
  defaultTitle?: string;
  defaultContent?: string;
  defaultContentHtml?: string;
  onClose: () => void;
  onConfirm?: (values: IKnowledgeBaseAddFormValues, goAfter?: boolean) => void;
}

const KnowledgeBaseAddModal: React.FC<IKnowledgeBaseAddModalProps> = ({
  visible,
  defaultTitle = '',
  defaultContent = '',
  defaultContentHtml = '',
  onClose,
  onConfirm,
}) => {
  const { formatMessage, locale } = useIntl();
  const [activeTab, setActiveTab] = useState<TKnowledgeAddTab>('existing');
  const [space, setSpace] = useState<TKnowledgeSpace>('personal');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetFields, setResetFields] = useState<string | undefined>();
  const goAfterRef = useRef(false);
  const searchFormRef = useRef<ISearchRef | null>(null);

  const {
    loading: spaceLoading,
    teamOptions,
    kbOptions,
    resolveSubmitTenantId,
  } = useKnowledgeBaseAddModalSpace({
    visible,
    activeTab,
    space,
    selectedTeamId,
  });

  const initialValues = useMemo(
    () => ({
      space: 'personal' as TKnowledgeSpace,
      teamId: '',
      kbId: '',
      kbName: '',
      kbDesc: '',
      title: defaultTitle || '',
      content: defaultContentHtml || defaultContent || '',
    }),
    [defaultTitle, defaultContent, defaultContentHtml],
  );

  const submitTenantId = useMemo(
    () => resolveSubmitTenantId(activeTab),
    [resolveSubmitTenantId, activeTab],
  );

  const { createKb, loading: createKbLoading } = useCreateKnowledgeBase(submitTenantId);
  const { createDocument, loading: createDocLoading } = useCreateKnowledgeDocument(submitTenantId);
  const {
    loading: kbModelsLoading,
    isReady: kbModelsReady,
    modelIds: kbDefaultModelIds,
  } = useKbDefaultModels(visible);

  /** UseFormSearch.resetFields：字段名变化时 form.change(name, undefined) */
  const triggerResetFields = useCallback((fields: string[]) => {
    if (!fields.length) return;
    setResetFields(undefined);
    fields.forEach((name, index) => {
      window.setTimeout(() => setResetFields(name), index * 16);
    });
  }, []);

  const canListKbOptions =
    space === 'personal' || Boolean(selectedTeamId);
  const kbOptionsEmpty =
    activeTab === 'existing'
    && !spaceLoading
    && canListKbOptions
    && kbOptions.length === 0;

  /** 团队空间列表是否为空（无可用团队） */
  const hasTeamSpaces = teamOptions.length > 0;

  const handleSpaceChange = useCallback((next: TKnowledgeSpace) => {
    setSpace(next);
    setSelectedTeamId('');
    triggerResetFields(next === 'team' ? ['kbId', 'kbName', 'teamId'] : ['kbId', 'kbName']);
  }, [triggerResetFields]);

  const handleTeamChange = useCallback((teamId: string) => {
    setSelectedTeamId(teamId);
    triggerResetFields(['kbId', 'kbName']);
  }, [triggerResetFields]);

  const { config } = useKnowledgeBaseAddFormConfig({
    activeTab,
    space,
    onSpaceChange: handleSpaceChange,
    onTeamChange: handleTeamChange,
    kbOptions,
    kbLoading: spaceLoading,
    kbOptionsEmpty,
    teamOptions,
    hasTeamSpaces,
    contentHtml: defaultContentHtml,
  });

  const handleTabChange = useCallback((tab: TKnowledgeAddTab): void => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setSpace('personal');
    setSelectedTeamId('');
    setResetFields(undefined);
  }, [activeTab]);


  const showKbSaveError = useCallback(
    (apiError?: string) => {
      downDataTip(
        'error',
        apiError || formatMessage({ id: 'dba_kb_add_failed' }),
      );
    },
    [formatMessage],
  );

  const handleFormSubmit = useCallback(
    async (formValues: unknown) => {
      const values = formValues as Record<string, unknown>;
      const trimTitle = String(values.title ?? '').trim();
      const rawContent = String(values.content ?? '');
      const trimmedRaw = rawContent.trim();
      const trimContent = isEditableHtmlEmpty(rawContent)
        ? ''
        : (/<[a-z]/i.test(trimmedRaw)
          ? (await convertHtmlToMarkdown(rawContent)).trim()
          : stripAnswerProtocolTags(trimmedRaw));

      setIsSubmitting(true);
      try {
        const formSpace = String(values.space || 'personal') as TKnowledgeSpace;
        let navigateKbId = '';
        let navigateTenantId = '';
        let navigateKnowledgeId = '';

        if (activeTab === 'existing') {
          let kbId = String(values.kbId ?? '');

          if (!kbId && kbOptionsEmpty) {
            const kbName = String(values.kbName ?? '').trim();
            if (!kbName) return;
            if (!kbDefaultModelIds) return;

            const kbResult = await createKb(
              buildDefaultCreateKbPayload({
                kbName,
                kbDefaultModelIds,
              }),
            );
            if (!kbResult.ok) {
              showKbSaveError(kbResult.error);
              return;
            }
            kbId = kbResult.id;
            navigateKbId = kbId;
            navigateTenantId =
              kbResult.tenantId ?? resolveSubmitTenantId('new') ?? '';
          }

          if (!kbId) return;

          const docTenantId = resolveSubmitTenantId('existing', kbId);
          const docResult = await createDocument(
            kbId,
            { title: trimTitle, content: trimContent, status: 'publish' },
            docTenantId,
          );
          if (!docResult.ok) {
            showKbSaveError(docResult.error);
            return;
          }
          navigateKbId = docResult.kbId ?? kbId;
          navigateTenantId = docResult.tenantId ?? docTenantId ?? '';
          navigateKnowledgeId = docResult.knowledgeId ?? '';
        } else {
          if (!kbDefaultModelIds) return;

          const kbName = String(values.kbName ?? '').trim();
          const aiEnabled = !!values.aiEnabled;

          const kbPayload = buildDefaultCreateKbPayload({
            kbName,
            kbDefaultModelIds,
            aiEnabled,
            description: String(values.kbDesc ?? '').trim() || undefined,
          });
          const kbResult = await createKb(kbPayload);
          if (!kbResult.ok) {
            showKbSaveError(kbResult.error);
            return;
          }

          // 创建初始文档（与 existing Tab 一致，参照 WeKnora 后端接口约定）
          if (trimTitle || trimContent) {
            const docTenantId = kbResult.tenantId ?? resolveSubmitTenantId('new');
            const docResult = await createDocument(
              kbResult.id,
              { title: trimTitle, content: trimContent, status: 'publish' },
              docTenantId,
            );
            if (!docResult.ok) {
              showKbSaveError(docResult.error);
              return;
            }
            navigateKnowledgeId = docResult.knowledgeId ?? '';
          }

          navigateKbId = kbResult.id;
          navigateTenantId =
            kbResult.tenantId
            ?? resolveSubmitTenantId('new')
            ?? '';
        }

        const goAfter = goAfterRef.current;
        goAfterRef.current = false;
        onConfirm?.({
          space: formSpace,
          title: trimTitle,
          content: trimContent,
          ...(formSpace === 'team' && values.teamId
            ? { teamId: String(values.teamId) }
            : {}),
        } satisfies IKnowledgeBaseAddFormValues, goAfter);
        const kbDisplayName = resolveKbDisplayName(
          activeTab,
          values,
          kbOptions,
          kbOptionsEmpty,
        );
        downDataTip(
          'success',
          formatMessage({ id: 'dba_kb_add_success' }, { kbName: kbDisplayName }),
        );
        if (goAfter && navigateKbId && navigateTenantId && navigateKnowledgeId) {
          handleOPenUrl({
            url: buildGameWikiKbDetailUrl({
              tenantId: navigateTenantId,
              knowledgeBaseId: navigateKbId,
              knowledgeId: navigateKnowledgeId,
            }),
          });
        }
        onClose();
      } finally {
        setIsSubmitting(false);
      }
    },
    [activeTab, kbOptionsEmpty, createKb, createDocument, onClose, onConfirm, formatMessage, convertHtmlToMarkdown, resolveSubmitTenantId, kbOptions, showKbSaveError, kbDefaultModelIds],
  );

  const formNode = (
    <KbAddFormSearch
      key={`${activeTab}-${defaultTitle}-${defaultContentHtml}`}
      config={config}
      initialValues={initialValues}
      searchFormRef={searchFormRef}
      resetFields={resetFields}
      onSubmit={handleFormSubmit}
    />
  );

  const isCreateKbModelsBlocked =
    (activeTab === 'new' || kbOptionsEmpty)
    && (kbModelsLoading || !kbModelsReady);
  const isDisabled =
    isSubmitting
    || createKbLoading
    || createDocLoading
    || spaceLoading
    || isCreateKbModelsBlocked;

  return (
    <Modal
      visible={visible}
      caption={formatMessage({ id: 'dba_kb_add_modal_caption' })}
      onClose={onClose}
      className={`dba-kb-modal ${locale === LANGUAGE_EN ? 'dba-kb-modal--en' : ''}`}
      size={900}
    >
      <Modal.Body>
        <div className="dba-kb-modal__segments">
          <button
            type="button"
            className={`dba-kb-modal__segment ${activeTab === 'existing' ? 'dba-kb-modal__segment--active' : ''}`}
            onClick={() => handleTabChange('existing')}
          >
            <span className="dba-kb-modal__segment-text">
              {formatMessage({ id: 'dba_kb_add_tab_existing' })}
            </span>
          </button>
          <button
            type="button"
            className={`dba-kb-modal__segment ${activeTab === 'new' ? 'dba-kb-modal__segment--active' : ''}`}
            onClick={() => handleTabChange('new')}
          >
            <span className="dba-kb-modal__segment-text">
              {formatMessage({ id: 'dba_kb_add_tab_new' })}
            </span>
          </button>
        </div>
        <div className="dba-kb-modal__form-wrap">{formNode}</div>
      </Modal.Body>
      <Modal.Footer>
        <div className="dba-kb-modal__footer-btns">
          <button
            type="button"
            className="dba-kb-modal__btn dba-kb-modal__btn--primary"
            disabled={isDisabled}
            onClick={() => {
              goAfterRef.current = false;
              searchFormRef.current?.handleSubmit?.();
            }}
          >
            {formatMessage({ id: 'dba_kb_add_confirm' })}
          </button>
          <button
            type="button"
            className="dba-kb-modal__btn dba-kb-modal__btn--secondary dba-kb-modal__btn--go"
            disabled={isDisabled}
            onClick={() => {
              goAfterRef.current = true;
              searchFormRef.current?.handleSubmit?.();
            }}
          >
            {formatMessage({ id: 'dba_kb_add_confirm_go' })}
          </button>
          <button
            type="button"
            className="dba-kb-modal__btn dba-kb-modal__btn--secondary"
            onClick={onClose}
          >
            {formatMessage({ id: 'dba_cancel' })}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default KnowledgeBaseAddModal;
